import React, { useState, useMemo } from "react";
import Modal from "../../../components/common/Modal";
import InputField from "../../../components/InputField";
import Button from "../../../components/Button";
import { validatePrice } from "../../../utils/validations";

import {
  VariantDataProps,
  VariantMiniBlockWithImageProps,
} from "../../../types";
import { v4 as uuidv4 } from "uuid";
import ImageUpload from "../../../components/ImageUpload";
import { removeExtraSpacesfromObject } from "../../../utils/helper";

type MiniBlockWithImgPopupProps = {
  handleClose: () => void;
  setVariantData: React.Dispatch<React.SetStateAction<VariantDataProps>>;
  data?: VariantMiniBlockWithImageProps;
  task?: "copy" | "edit";
};

// MINI_BLOCK_WITH_IMAGE Option Schema
// {
//   "image": "products/bd0b7d82-24b4-46cc-a19b-57cc12a41368.jpg",
//   "product_image": "products/bd0b7d82-24b4-46cc-a19b-57cc12a41368.jpg",
//   "name": "90",
//   "price": 50,
//   "code": "A354"
// }

const initState = {
  image: "",
  product_image: "",
  name: "",
  code: null,
  price: 0,
  type: "MINI_BLOCK_WITH_IMAGE",
};

export default function MiniBlockWithImgPopup({
  handleClose,
  setVariantData,
  data = initState,
  task,
}: MiniBlockWithImgPopupProps) {
  const [miniBlockWithImgData, setMiniBlockWithImgData] =
    useState<VariantMiniBlockWithImageProps>(data);
  const [errorMessage, setErrorMessage] = useState<any>({
    image: [],
    product_image: [],
    name: [],
    code: [],
    price: [],
  });

  const handleSubmitData = () => {
    if (!miniBlockWithImgData?.name || !miniBlockWithImgData?.image) {
      if (!miniBlockWithImgData?.name) {
        setErrorMessage((e: any) => ({
          ...e,
          name: ["Please enter the name."],
        }));
      }
      if (!miniBlockWithImgData?.image) {
        setErrorMessage((e: any) => ({
          ...e,
          image: ["Please select an image."],
        }));
      }
      return;
    }
    const clone: any = removeExtraSpacesfromObject(
      structuredClone(miniBlockWithImgData)
    );

    if (
      clone.price === "" ||
      (clone.price && +clone.price == 0) ||
      !clone.price
    ) {
      clone.price = "0";
    }
    if (clone.price) {
      clone.price = parseFloat(clone.price);
    }

    if (data.id && task !== "copy") {
      // Edit Option Functionality
      setVariantData((prev) => ({
        ...prev,
        options: prev.options.map((el: any) =>
          el.id === data.id ? clone : el
        ),
      }));
    } else {
      // Add Option Functionality
      const copyID = data.id;
      const newData = { ...clone, id: uuidv4(), added: true };
      setVariantData((prev) => {
        if (task === "copy") {
          const findIndex = prev.options.findIndex(
            (option) => option?.id === copyID
          );
          const updatedOptions = [...prev.options];
          updatedOptions.splice(findIndex + 1, 0, newData);
          return {
            ...prev,
            options: updatedOptions,
          };
        } else {
          const deleteIndex = prev.options.findIndex(
            (option) => option?.to_delete
          );

          if (deleteIndex === -1) {
            return {
              ...prev,
              options: [...prev.options, newData],
            };
          }

          const updatedOptions = [...prev.options];
          updatedOptions.splice(deleteIndex, 0, newData);
          return {
            ...prev,
            options: updatedOptions,
          };
        }
      });
    }

    setMiniBlockWithImgData(initState);
    handleClose();
  };

  const imageList = useMemo(
    () => (miniBlockWithImgData.image ? [miniBlockWithImgData.image] : []),
    [miniBlockWithImgData.image]
  );
  const productImageList = useMemo(
    () =>
      miniBlockWithImgData.product_image
        ? [miniBlockWithImgData.product_image]
        : [],
    [miniBlockWithImgData.product_image]
  );
  return (
    <Modal
      allowScroll
      header={
        task === "copy"
          ? "Add an Option"
          : data.name
          ? "Edit Option"
          : "Add an Option"
      }
      footer={
        <Button
          label={
            task === "copy"
              ? "Add Option"
              : miniBlockWithImgData.id
              ? "Update"
              : "Add Option"
          }
          variant="secondary"
          onClick={handleSubmitData}
        />
      }
      handleCancel={handleClose}
    >
      <div className="w-full h-full flex flex-col gap-3">
        {/* code */}
        <InputField
          onChange={(e) => {
            setMiniBlockWithImgData((prevData) => ({
              ...prevData,
              code: e?.toUpperCase(), // Set code to null if empty, otherwise to uppercase value
            }));
            setErrorMessage((e: any) => ({ ...e, code: [] }));
          }}
          value={miniBlockWithImgData.code}
          placeholder="Code"
          label="Code:"
          hint={errorMessage?.code}
          maxLength={10}
        />

        {/* name */}
        <InputField
          onChange={(e) => {
            setMiniBlockWithImgData((prevData) => ({
              ...prevData,
              name: e,
            }));
            setErrorMessage((e: any) => ({ ...e, name: [] }));
          }}
          value={miniBlockWithImgData.name}
          placeholder="Name"
          label="Name:"
          hint={errorMessage?.name}
          required
        />

        {/* price */}
        <InputField
          onChange={(e: any) => {
            if (e > 99999) {
              setErrorMessage((prevError: any) => ({
                ...prevError,
                price: ["Price should be less than or equal to 99999."],
              }));
            } else {
              setMiniBlockWithImgData((prevData) => ({
                ...prevData,
                price: e,
              }));
              setErrorMessage((prevError: any) => ({
                ...prevError,
                price: [],
              }));
            }
          }}
          value={miniBlockWithImgData.price}
          placeholder="Price"
          label="Price:"
          type="price"
          validate={validatePrice}
          hint={errorMessage.price}
        />

        {/* image */}
        <ImageUpload
          required
          label="Upload image:"
          imageList={imageList}
          setter={(value) => {
            miniBlockWithImgData.image = value as string;
            setMiniBlockWithImgData(structuredClone(miniBlockWithImgData));
          }}
          kind="PRODUCT"
          errors={errorMessage?.image}
          removeError={() => setErrorMessage((e: any) => ({ ...e, image: [] }))}
        />

        {/* product_image */}
        <ImageUpload
          label="Upload Product Image:"
          imageList={productImageList}
          setter={(value) => {
            setMiniBlockWithImgData((prev) => ({
              ...prev,
              product_image: value as string,
            }));
          }}
          kind="PRODUCT"
          errors={errorMessage?.product_image}
          removeError={() =>
            setErrorMessage((e: any) => ({ ...e, product_image: [] }))
          }
        />
      </div>
    </Modal>
  );
}
